import entities from '../../entities/index';
import store from '../../store/store';
import ENTITIES from '../../store/actions/index';
import util from "../../lib/util";
import * as LookupActions from "../../store/actions/lookupActions";
import {existsFilter} from "../../lib/picos/filters";
import Lookup from "../../entities/lookup";
import type {EntityType, uuid} from "../../@types/entity";

export type LookupTable = {
    path: string,
    entityName: string,
    fieldName: string,
    storeName: string,
    saveMethod: Function,
    additionalData: any
}
/**
 * This class manages a registry of lookup users.
 *
 * The purpose of this class is to assist with deletes and merges of lookup keys.
 * This is problematic because we need to know every place in the user interface
 * that uses a particular lookup path, and check for the existence of the key
 * to be deleted.
 */
class LookupRegistry {
    lookupTable: Array<LookupTable> = [];

    static SACROSANCT = {
        CLIENT: '442ada96-c371-4701-a021-1a82d619c066', // note:tag 'import' used by ImportExport
        IMPORT: '995f186b-a69a-48ea-bba4-9344af4be7a9', // any:participant:relationship 'Client' used by Participants
        BENEFACTOR: '73d54d07-2016-4a9e-9c89-ad843d93e178', // any:participant:relationship
        CONFLICT: '6564fdc0-c7fe-408f-b4da-5c84a65117c7' // milestone:reasons:refused
    };

    /**
     * Seeded any:participant:relationship roles that make a participant adverse to the
     * firm's client. They ship with the same lookupId in every firm, so they are always
     * adverse, whatever a firm renames them to. Roles a firm creates itself are never
     * adverse, whatever they are named (John, 2026-09-24). Opposing counsel's staff joined
     * the list with OWA-180; the court roles (Court Clerk, Court Administrator) stay neutral.
     * The server enforces the same IDs (server/src/schemas/participant.ts invariants).
     */
    static ADVERSE_RELATIONSHIPS: uuid[] = [
        '53eb1870-58b4-4f4f-a592-9672d940420b', // Opposing Party
        '167b6b1b-9155-4b3c-8063-9a31d8c6c6b3', // Opposing Counsel
        '2f9c4d7b-5eaa-4591-add6-b4da1159af74', // Opposing Counsel's Paralegal
        '6507b60e-3005-43b4-8301-b16cca9140f4'  // Opposing Counsel's Legal Assistant
    ];

    /**
     * Is this relationship adverse to the client? A participant who is adverse must
     * never also be a Benefactor: benefactors receive invoices and payment links, and
     * an invoice carries privileged narrative that an opposing party may not see.
     *
     * @param {uuid} lookupId
     * @return {boolean}
     */
    static isAdverseRelationship(lookupId: uuid): boolean {
        return LookupRegistry.ADVERSE_RELATIONSHIPS.includes(lookupId);
    }

    /**
     * Does this relationship list make a participant both a Benefactor and adverse? Such a
     * participant may not be saved, and is never treated as a benefactor (invoice email,
     * payment link, payer lists). Decided from the IDs, so a missing lookup cannot hide a
     * seeded adverse role.
     *
     * @param {Array.<uuid>} lookupIds
     * @return {boolean}
     */
    static hasBenefactorConflict(lookupIds: uuid[] | undefined): boolean {
        return !!lookupIds?.includes(LookupRegistry.SACROSANCT.BENEFACTOR)
               && lookupIds.some(id => LookupRegistry.isAdverseRelationship(id));
    }

    /**
     *
     * @param {uuid} lookupId
     * @return {Lookup}
     */
    static getLookupById(lookupId: uuid): Lookup {
        return store.getState().lookups.find(lookup => lookup.id === lookupId);
    }

    /**
     * Build up the list of paths/entities/names.
     *
     * @constructor
     */
    constructor() {
        // Scan all static schemas for lookupPaths.
        // Need the entity name and field, and a list of their paths.
        entities.getAll().forEach(entity => {
            // Get schema info for this entity (e.g. Milestone)
            const schemaInfo = entity.getSchema();

            // Go through the entity's fields, looking for a field named lookupPaths.
            // Such a field will contain an array of paths, e.g. ['this:that', 'this:other'].
            Object.entries(schemaInfo.fields).forEach(field => {

                // Check for the lookupPaths field
                const [entityFieldName, entityFields] = field;
                if (Object.prototype.hasOwnProperty.call(entityFields, 'lookupPaths')) {

                    // Found one!  Add its paths to the registry!
                    const lookupPaths = entityFields['lookupPaths'];

                    // Also check for custom fields
                    const additionalData = entityFields['additionalData'];

                    // Find the plural name that is used with redux store (e.g. activities for activity)
                    const ENTITY = ENTITIES.find(e => e.name === schemaInfo.entity);
                    const storeName = ENTITY.plural;

                    // Get the redux saveQueue method for this entity.
                    const saveMethod = ENTITY.saveQueue;

                    // Push the path, entity's name, field name, storeName and saveMethod to the lookupTable
                    // Remember that each member of lookupPaths will be a string.
                    if (Array.isArray(lookupPaths)) {
                        lookupPaths.forEach(path => this.lookupTable.push({
                            path,
                            entityName: schemaInfo.entity,
                            fieldName: entityFieldName,
                            additionalData: additionalData,
                            storeName,
                            saveMethod
                        }));
                    }
                }
            });
        });

        // OWA-1589: questions and custom fields add no lookup uses. A Question keeps its choices as plain
        // strings and an Answer's value is a string read by the question's answerType; neither stores a
        // lookup id, so there is nothing to register here and nothing for a lookup delete to follow.
    }

    /**
     * Get the additionalData structure for the given path.
     *
     * @param {string} path - the path (e.g. contact:event:category)
     */
    getLookupTableEntry = (path: string): LookupTable => {
        // Get the lookup table entry for a given path.
        return this.lookupTable.find(entry => entry.path === path)?.additionalData;
    };

    /**
     * Internal method to do the hard work.
     *
     * To make this more clear, comments will refer to an example as follows:
     * - we are merging two lookups used by the Event object.  Contact uses Event as a ref field (events: uuid[]),
     *   so we will need to update all of the Contacts' events fields with the updated Event objects.
     *
     * @param {Lookup} lookup - this is the source lookup (e.g. Name1), that will be replaced with the targetId
     * @param {uuid} [targetId] - (optional) replacement UUID if merging (e.g. Name2)
     * @private
     */
    _doWork = (lookup: Lookup, targetId?: uuid) => {
        // The lookup contains two fields of interest: path and lookupId.  The path will be something like contact:event:category,
        // which means that the category field of the event object, which is located within the contact object.
        // This is the lookup object for Name1.
        //
        // The lookupId is the uuid of the replacement, in this case the lookupId of Name2
        const {path, lookupId} = lookup;

        // If we need to update a bunch of objects, we need to make sure we have unique timestamps. We create a base and keep adding one
        // so that we never get the same timestamp twice (fast processors do this every time).
        let timestamp = util.newHRTimestamp();

        // Get a list of lookupTable entries with a matching path.  lookupTable entries are NOT lookup objects.  They are just a record
        // with some handy fields that we can use.
        const lookupEntries = this.lookupTable.filter(l => l.path === path);

        // For each path, we need to load each entity instance, search its fieldName field for the given key.
        // If found, we need to splice out the offending entry, update the entity instance with the new info,
        // then save it to the database via redux.
        // e.g. we need to find every Event object that has the lookupId (Name1), and replace it with the targetId (Name2).

        // Get all instances of the entity.  These will be lookup objects.
        lookupEntries.filter(existsFilter)
                     .forEach(lookupEntry => {
                         // Get the associated store and cycle through every entry in the store
                         // lookupEntry.storeName is really just the plural of the entity name, e.g. Events.
                         // Target will thus be an Event object.
                         store.getState()[lookupEntry.storeName].forEach((t: EntityType) => {
                             const target = t.clone();

                             // Check for the source lookup id in the target field (field will either be an array or null).
                             // targetField will either be a uuid or uuid[].
                             // e.g. target will be event.category, which is just a uuid.
                             const targetField = target[lookupEntry.fieldName];

                             // Keep track of whether we made a change. That will determine if we need to save the parent as well.
                             let changeMade = false;

                             // Ignore nulls/empty arrays
                             // If an array and has at least one entry
                             if (Array.isArray(targetField) && targetField.length > 0) {

                                 // Look for the lookupId in targetField
                                 if (targetField.includes(lookupId)) {

                                     // Strip out the key
                                     const targetCopy = [...targetField];
                                     const index = targetCopy.indexOf(lookupId);

                                     if (targetId) targetCopy.splice(index, 1, targetId); // replace
                                     else targetCopy.splice(index, 1); // delete

                                     // Update the entity
                                     target[lookupEntry.fieldName] = targetCopy;
                                     target.status = 'U';
                                     target.addedOn = util.newHRTimestamp();
                                     target.addedBy = sessionStorage.getItem('userId');

                                     // Update!
                                     store.dispatch(lookupEntry.saveMethod(target));

                                     changeMade = true;
                                 }
                             }
                             else {
                                 // Just a single value, replace or set to null (if no targetId)
                                 if (targetField === lookupId) {
                                     // Update the entity
                                     target[lookupEntry.fieldName] = targetId ? targetId : null;
                                     target.status = 'U';
                                     target.addedOn = util.newHRTimestamp();
                                     target.addedBy = sessionStorage.getItem('userId');

                                     // Update!
                                     store.dispatch(lookupEntry.saveMethod(target));

                                     changeMade = true;
                                 }
                             }

                             // If we made a change, we might need to change the parent.  We need to look at the target field to find it's parent.
                             // We can do that easily by looking at the entityType and entityId fields.  The entityType will be something like 'contact',
                             // and the entityId will be the id of the parent.
                             if (changeMade) {
                                 // Get the parent's entry in the ENTITY table.  This contains things like the entity name, plural name, and save methods.
                                 const parentENTITY = ENTITIES.find(l => l.name === target.entityType);

                                 // Find the parent of the entry we just changed
                                 const parent = store.getState()[parentENTITY.plural].find((ent: EntityType) => ent.id === target.entityId);

                                 if (parent) {
                                     // Force an update by updating the timestamp (leave everything else the same!)
                                     timestamp = timestamp + 1;

                                     // Since this is a ref field, it's info is stale, as we only updated the refs themselves!
                                     // To remedy this, we need to update the state of this parent, and broadcast it (thus up the timestamp).
                                     // For reference fields, there is always a member of the parent object with the plural name of the reference.
                                     // We only support uuid[], so we will never have a ref field with only one entry.  This makes things easier,
                                     // as the plural is nothing more than the storeName for the lookupEntry with an underscore prefix.
                                     const parentField = [...parent[lookupEntry.storeName]];

                                     // Find and replace/delete the field (e.g. Event)
                                     const index = parentField.findIndex(entry => entry.id === target.id);

                                     if (index > -1) {
                                         if (targetId) parentField.splice(index, 1, target);
                                         else parentField.splice(index, 1);

                                         parent.addedOn = timestamp;
                                         parent[`_${lookupEntry.storeName}`] = parentField;

                                         // Update the parent
                                         store.dispatch(parentENTITY.saveQueue(parent));
                                     }
                                     else console.error(`Failed to find ${target.id} in`, parentField);
                                 }
                                 else console.warn(`entityId was null (zeros) for ${target.entityType}`, target);
                             }
                         });
                     });

        // Finally, delete this key from the lookup table for this path.
        const lu = lookup.clone();
        lu.status = 'D';
        lu.addedOn = util.newHRTimestamp();
        lu.addedBy = sessionStorage.getItem('userId');

        store.dispatch(LookupActions.removeLookupQueue(lu));
    };

    /**
     * Find each entity/object where we need to look for the passed key and delete it/save the changes.
     *
     * @param {Lookup} lookup
     */
    doDelete = (lookup: Lookup) => {
        this._doWork(lookup);
    };

    /**
     * Merge two lookups into one.
     * @param {Lookup} lookup - lookup to be merged
     * @param {uuid} targetId - Lookup to merge in to
     */
    doMerge = (lookup: Lookup, targetId: uuid) => {
        this._doWork(lookup, targetId);
    }
}

export default LookupRegistry;
